/**
 * DomainMarketplacePage
 *
 * Browse published domain listings, see pricing, and enroll.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { marketplaceAPI, characterAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import CatalogCard from '../../../shared/components/CatalogCard';
import CharacterPickerModal from '../components/CharacterPickerModal';
import LearnerNav from '../components/nav/LearnerNav';

function DomainCard({ listing, onEnroll, enrolled }) {
  // Check per-track price_model from domain_listings; fall back to top-level pricing.
  // Show "Certificate Program" if any track is paid; "Free" only when all are free.
  const hasPaidTrack = Array.isArray(listing.tracks)
    ? listing.tracks.some((tr) => tr.price_model && tr.price_model !== 'free')
    : false;
  const topLevelFree = !listing.pricing || listing.pricing.pricing_model === 'free';
  const priceText = hasPaidTrack
    ? 'Certificate Program'
    : topLevelFree
      ? 'Free'
      : `$${(listing.pricing.price_cents / 100).toFixed(2)}/${listing.pricing.pricing_model === 'subscription' ? 'mo' : 'one-time'}`;

  const levelBadge = listing.level || listing.difficulty;

  return (
    <CatalogCard
      title={listing.title}
      description={listing.short_description}
      icon={listing.thumbnail_url || '🎓'}
      badges={levelBadge ? [{ label: levelBadge }] : []}
      tags={listing.tags || []}
      footer={{ left: priceText, right: enrolled ? '✓ Enrolled' : '' }}
      actions={enrolled ? [] : [{ label: 'Enroll', variant: 'primary', onClick: () => onEnroll(listing) }]}
    />
  );
}

export default function DomainMarketplacePage() {
  const { learner } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [search, setSearch] = useState('');
  // Character archetype picker state
  const [archetypeModal, setArchetypeModal] = useState(null); // { domainId, characterClasses, characterDisplayName, pendingNav }

  useEffect(() => {
    Promise.all([
      marketplaceAPI.listDomains(),
      learner ? marketplaceAPI.getSubscriptions(learner.learner_id) : Promise.resolve({ data: { subscriptions: [] } }),
    ])
      .then(([listRes, subRes]) => {
        setListings(listRes.data.listings || []);
        setSubscriptions(subRes.data.subscriptions || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [learner]);

  const isEnrolled = (domainId) => subscriptions.some((s) => s.domain_id === domainId && s.status !== 'expired');

  // Navigate to character-select, optionally after archetype selection
  const goToCharacterSelect = (domainId) => navigate(`/character-select/${domainId}`);

  const handleArchetypeSelect = async (classId) => {
    if (!archetypeModal) return;
    try {
      await characterAPI.selectCharacter(archetypeModal.domainId, classId);
    } catch {
      // non-fatal — proceed even if the store call fails
    }
    setArchetypeModal(null);
    goToCharacterSelect(archetypeModal.domainId);
  };

  const handleArchetypeSkip = async (defaultClassId) => {
    if (!archetypeModal) return;
    if (defaultClassId) {
      try {
        await characterAPI.selectCharacter(archetypeModal.domainId, defaultClassId);
      } catch {
        // non-fatal
      }
    }
    setArchetypeModal(null);
    goToCharacterSelect(archetypeModal.domainId);
  };

  const handleEnroll = async (listing) => {
    if (!learner) { navigate('/login'); return; }
    setEnrolling(listing.domain_id);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await marketplaceAPI.enroll({ learner_id: learner.learner_id, domain_id: listing.domain_id });
      const status = res?.data?.status;
      if (status === 'already_enrolled') {
        setSuccessMsg(`You're already enrolled in "${listing.title}". Taking you there now…`);
      } else {
        setSuccessMsg(`Successfully enrolled in "${listing.title}"! Checking learning paths…`);
      }
      setSubscriptions((prev) => [...prev, { domain_id: listing.domain_id, status: 'free' }]);

      // Check if this domain has character archetypes enabled
      try {
        const { data: archetypeData } = await characterAPI.getCharacterClasses(listing.domain_id);
        if (archetypeData?.character_archetypes_enabled && archetypeData?.character_classes?.length > 0) {
          // Show the archetype picker — navigation happens after the modal resolves
          setArchetypeModal({
            domainId: listing.domain_id,
            characterClasses: archetypeData.character_classes,
            characterDisplayName: archetypeData.character_display_name || 'Learning Path',
          });
          return; // don't navigate yet — modal will handle it
        }
      } catch {
        // If the check fails, fall through to default navigation
      }

      setTimeout(() => goToCharacterSelect(listing.domain_id), 1000);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.message === 'Payment required') {
        navigate(`/enroll/${listing.domain_id}`, { state: { listing, pricing: detail } });
      } else {
        setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
    } finally {
      setEnrolling(null);
    }
  };

  const filtered = listings.filter((l) =>
    !search || l.title.toLowerCase().includes(search.toLowerCase()) ||
    l.tags?.some((t) => t.toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) return <div className="dmp-loading">Loading marketplace...</div>;

  return (
    <div className="dmp-page">
      <LearnerNav />
      {/* Character archetype picker — shown after enrollment when archetypes are enabled */}
      {archetypeModal && (
        <CharacterPickerModal
          domain={archetypeModal.domainId}
          characterClasses={archetypeModal.characterClasses}
          characterDisplayName={archetypeModal.characterDisplayName}
          onSelect={handleArchetypeSelect}
          onSkip={handleArchetypeSkip}
        />
      )}
      <div className="dmp-header">
        <button className="dmp-back-btn" onClick={() => navigate('/')}>← Dashboard</button>
        <h2>Domain Marketplace</h2>
      </div>

      <div className="dmp-search-row">
        <input
          className="dmp-search"
          type="text"
          placeholder="Search domains..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {successMsg && <div className="dmp-success">{successMsg}</div>}
      {error && <div className="dmp-error">{error}</div>}

      {filtered.length === 0 ? (
        <div className="dmp-empty">No domains available yet. Check back soon!</div>
      ) : (
        <div className="dmp-grid">
          {filtered.map((l) => (
            <DomainCard
              key={l.domain_id}
              listing={l}
              enrolled={isEnrolled(l.domain_id)}
              onEnroll={enrolling ? () => {} : handleEnroll}
            />
          ))}
        </div>
      )}

      <style>{`
        .dmp-page { min-height: 100vh; background: #0f172a; color: #e2e8f0; padding: 2rem; font-family: system-ui, sans-serif; }
        .dmp-loading { display: flex; justify-content: center; align-items: center; min-height: 100vh; color: #94a3b8; }
        .dmp-header { display: flex; align-items: center; gap: 1.5rem; margin-bottom: 1.5rem; }
        .dmp-back-btn { background: none; border: 1px solid rgba(255,255,255,0.2); color: #94a3b8; padding: 0.4rem 0.8rem; border-radius: 0.5rem; cursor: pointer; }
        .dmp-header h2 { margin: 0; font-size: 1.5rem; }
        .dmp-search-row { margin-bottom: 1.5rem; }
        .dmp-search { width: 100%; max-width: 480px; padding: 0.6rem 1rem; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.15); border-radius: 0.5rem; color: #e2e8f0; font-size: 0.95rem; }
        .dmp-success { background: rgba(52,211,153,0.15); border: 1px solid rgba(52,211,153,0.4); padding: 0.75rem 1rem; border-radius: 0.5rem; margin-bottom: 1rem; color: #6ee7b7; }
        .dmp-error { background: rgba(239,68,68,0.15); border: 1px solid rgba(239,68,68,0.4); padding: 0.75rem 1rem; border-radius: 0.5rem; margin-bottom: 1rem; color: #fca5a5; }
        .dmp-empty { color: #64748b; text-align: center; padding: 3rem; }
        .dmp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1.5rem; }
        .dmp-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 1rem; overflow: hidden; transition: border-color 0.2s; }
        .dmp-card:hover { border-color: #6366f1; }
        .dmp-card-img { width: 100%; height: 160px; object-fit: cover; }
        .dmp-card-body { padding: 1.25rem; display: flex; flex-direction: column; gap: 0.6rem; }
        .dmp-card-title { margin: 0; font-size: 1.1rem; font-weight: 700; }
        .dmp-card-desc { font-size: 0.85rem; color: #94a3b8; margin: 0; }
        .dmp-card-tags { display: flex; gap: 0.4rem; flex-wrap: wrap; }
        .dmp-tag { font-size: 0.65rem; padding: 0.1rem 0.4rem; background: rgba(99,102,241,0.15); color: #a78bfa; border-radius: 9999px; }
        .dmp-card-footer { display: flex; justify-content: space-between; align-items: center; }
        .dmp-price.free { color: #34d399; font-weight: 700; }
        .dmp-price.paid { color: #fbbf24; font-weight: 700; }
        .dmp-enroll-btn { padding: 0.4rem 0.9rem; background: #6366f1; color: #fff; border: none; border-radius: 0.5rem; cursor: pointer; font-weight: 600; }
        .dmp-enrolled-badge { color: #34d399; font-weight: 600; font-size: 0.85rem; }
      `}</style>
    </div>
  );
}
